import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WHOLESALE_BOARD,
  WHOLESALE_DOCUMENT_LABELS,
  WHOLESALE_METHOD_LABELS,
  WHOLESALE_PAYMENT_METHODS,
  WHOLESALE_STAGE_LABELS,
  WHOLESALE_STATUS_LABELS,
  type WholesaleCatalogItemDTO,
  type WholesaleDocument,
  type WholesaleOrderDTO,
  type WholesaleOrderStatus,
  type WholesalePaymentMethod,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { ArrowRight, Plus, Search, Store, Trash2, Truck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage, isApiError } from '../../lib/api';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import '../orders-chat/orders-chat.css';
import './wholesale.css';
import { OrderRemitoBlock } from './remcarne';
import {
  PrintNotesButton,
  STATUS_TONES,
  lineTotal,
  moneyInput,
  parseMoney,
  parseQty,
  qtyInput,
  qtyText,
  useStaff,
  useWsCustomers,
  useWsSettings,
} from './common';

/** Etapa siguiente desde la tarjeta (preparar abre el detalle para cargar los kilos reales). */
const FORWARD: Partial<Record<WholesaleOrderStatus, WholesaleOrderStatus>> = { PENDING: 'PREPARING', READY: 'ON_ROUTE', ON_ROUTE: 'DELIVERED' };

/** Tablero de pedidos mayoristas: pedido → preparación → listo → en reparto → entregado. */
export function WholesaleOrdersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const q = useQuery({
    queryKey: ['wholesale', 'orders', 'board', search],
    queryFn: () => api.get<WholesaleOrderDTO[]>(`/wholesale/orders?status=board${search ? `&search=${encodeURIComponent(search)}` : ''}`),
    refetchInterval: 30_000,
  });
  const move = useMutation({
    mutationFn: (v: { id: string; status: WholesaleOrderStatus }) =>
      api.patch<WholesaleOrderDTO>(`/wholesale/orders/${v.id}/status`, { status: v.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wholesale'] }),
    onError: (e) => toast.error('No se pudo mover', errorMessage(e)),
  });
  const rows = q.data ?? [];
  const open = params.get('p');

  return (
    <>
      <PageHeader
        title="Pedidos mayoristas"
        subtitle="Del pedido a la entrega. Al preparar se cargan los kilos reales: ahí se descuenta el stock y sale el remito o la factura."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
            Nuevo pedido
          </Button>
        }
      />
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, maxWidth: 420 }}>
        <Input placeholder="Buscar por cliente o número" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Buscar pedidos" />
      </div>
      {q.isLoading ? (
        <Skeleton style={{ height: 320 }} />
      ) : rows.length === 0 && !search ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={<Truck />}
              title="Todavía no hay pedidos mayoristas"
              text="Cargá el primero: elegís el cliente y el precio sale de su lista."
              action={
                <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                  Nuevo pedido
                </Button>
              }
            />
          </CardBody>
        </Card>
      ) : (
        <div className="bf-board" style={{ gridTemplateColumns: 'repeat(5, minmax(210px, 1fr))' }}>
          {WHOLESALE_BOARD.map((status) => {
            const list = rows.filter((o) => o.status === status);
            return (
              <section
                key={status}
                className={`bf-board-col is-${status === 'PREPARING' ? 'preparing' : status === 'READY' ? 'ready' : status === 'DELIVERED' ? 'done' : 'new'}`}
                aria-label={WHOLESALE_STATUS_LABELS[status]}
              >
                <h3>
                  <span>
                    <i className="bf-board-dot" aria-hidden /> {WHOLESALE_STATUS_LABELS[status]}
                  </span>
                  <span className="bf-board-count">{list.length}</span>
                </h3>
                {list.length === 0 && <p className="bf-board-empty">Sin pedidos</p>}
                {list.map((o) => (
                  <article
                    key={o.id}
                    className="bf-order-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => setParams({ p: o.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setParams({ p: o.id });
                      }
                    }}
                  >
                    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <strong>#{o.number}</strong>
                      {o.dueCents > 0 && o.status === 'DELIVERED' ? (
                        <Badge tone="warning">Debe {formatMoney(o.dueCents)}</Badge>
                      ) : o.paidCents > 0 && o.status !== 'DELIVERED' ? (
                        <Badge tone="success">Cobrado {formatMoney(o.paidCents)}</Badge>
                      ) : null}
                    </span>
                    <span className="bf-cell-main">{o.customerName}</span>
                    <span className="bf-cell-sub">
                      {o.fulfillment === 'DELIVERY' ? <Truck size={13} aria-hidden /> : <Store size={13} aria-hidden />}{' '}
                      {o.fulfillment === 'DELIVERY' ? (o.driverName ?? 'Sin repartidor') : 'Retira'}
                      {o.deliveryDate ? ` · ${formatDate(o.deliveryDate)}` : ''}
                    </span>
                    <span className="bf-cell-sub">
                      {o.items.length} {o.items.length === 1 ? 'producto' : 'productos'} ·{' '}
                      {o.totalCents !== null ? formatMoney(o.totalCents) : `≈ ${formatMoney(o.estimatedTotalCents)}`}
                    </span>
                    {FORWARD[o.status] && !(o.status === 'READY' && o.fulfillment === 'PICKUP') && (
                      <button
                        type="button"
                        className="bf-order-next"
                        onClick={(e) => {
                          e.stopPropagation();
                          move.mutate({ id: o.id, status: FORWARD[o.status]! });
                        }}
                      >
                        {WHOLESALE_STATUS_LABELS[FORWARD[o.status]!]} <ArrowRight size={13} aria-hidden />
                      </button>
                    )}
                  </article>
                ))}
              </section>
            );
          })}
        </div>
      )}
      {creating && (
        <OrderFormDialog
          onClose={() => setCreating(false)}
          onSaved={(o) => {
            setCreating(false);
            setParams({ p: o.id });
          }}
        />
      )}
      {open && <OrderDetailDialog id={open} onClose={() => setParams({})} />}
    </>
  );
}

interface Line {
  item: WholesaleCatalogItemDTO;
  qty: string;
  price: string;
}

/** Alta de un pedido: cliente, productos con el precio de su lista, entrega o retiro y comprobante. */
export function OrderFormDialog({
  onClose,
  onSaved,
  customerId: fixedCustomer,
}: {
  onClose: () => void;
  onSaved: (o: WholesaleOrderDTO) => void;
  customerId?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useSession();
  const manage = can('wholesale.manage');
  const editPrices = manage || can('prices.manage');
  const settings = useWsSettings();
  const staff = useStaff();
  const [who, setWho] = useState('');
  const customers = useWsCustomers(who.trim().length >= 2 ? who.trim() : '');
  const [customerId, setCustomerId] = useState(fixedCustomer ?? '');
  const [fulfillment, setFulfillment] = useState<'DELIVERY' | 'PICKUP'>('DELIVERY');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [driverUserId, setDriver] = useState('');
  const [doc, setDoc] = useState<WholesaleDocument | ''>('');
  const [branchId, setBranchId] = useState('');
  const [notes, setNotes] = useState('');
  const [find, setFind] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const catalog = useQuery({
    queryKey: ['wholesale', 'catalog', customerId],
    queryFn: () => api.get<WholesaleCatalogItemDTO[]>(`/wholesale/catalog/${customerId}`),
    enabled: !!customerId,
  });
  const customer = customers.data?.find((c) => c.id === customerId);
  const own = me.branches.filter((b) => !b.franchise);
  const matches = useMemo(() => {
    const t = find.trim().toLowerCase();
    if (!t) return [];
    return (catalog.data ?? []).filter((p) => p.name.toLowerCase().includes(t) && !lines.some((l) => l.item.productId === p.productId)).slice(0, 8);
  }, [find, catalog.data, lines]);
  const total = lines.reduce((t, l) => {
    const q = parseQty(l.item.unitType, l.qty) ?? 0;
    const p = parseMoney(l.price) ?? 0;
    return t + lineTotal(l.item.unitType, q, p);
  }, 0);

  const save = useMutation({
    mutationFn: () =>
      api.post<WholesaleOrderDTO>('/wholesale/orders', {
        customerId,
        branchId: branchId || null,
        fulfillment,
        deliveryDate: deliveryDate || null,
        driverUserId: fulfillment === 'DELIVERY' ? driverUserId || null : null,
        documentKind: doc || null,
        notes: notes || null,
        items: lines.map((l) => {
          const price = parseMoney(l.price);
          return {
            productId: l.item.productId,
            qty: parseQty(l.item.unitType, l.qty) ?? 0,
            unitPriceCents: price !== l.item.priceCents ? price : null,
          };
        }),
      }),
    onSuccess: async (o) => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success(`Pedido #${o.number} cargado`, o.customerName);
      onSaved(o);
    },
    onError: (e) => toast.error('No se pudo cargar', errorMessage(e)),
  });
  const missingPrice = lines.some((l) => parseMoney(l.price) === null);
  const badQty = lines.some((l) => !parseQty(l.item.unitType, l.qty));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Nuevo pedido mayorista" wide>
      <div style={{ display: 'grid', gap: 16 }}>
        {!fixedCustomer && (
          <Field label="Cliente">
            {customerId && customer ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <strong>{customer.tradeName ?? customer.name}</strong>
                <span className="bf-cell-sub">
                  {customer.effectivePriceList ? `Lista: ${customer.effectivePriceList.name}` : 'Precio del mostrador'}
                </span>
                <Button variant="ghost" onClick={() => (setCustomerId(''), setLines([]))}>
                  Cambiar
                </Button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                <Input placeholder="Escribí el nombre del cliente" value={who} onChange={(e) => setWho(e.target.value)} autoFocus />
                {(customers.data ?? []).slice(0, 6).map((c) => (
                  <button key={c.id} type="button" className="bf-pick" onClick={() => setCustomerId(c.id)}>
                    <strong>{c.tradeName ?? c.name}</strong>{' '}
                    <span className="bf-cell-sub">{[c.zoneName, c.segmentName].filter(Boolean).join(' · ')}</span>
                    {c.overLimit && <Badge tone="danger">Pasado del límite</Badge>}
                  </button>
                ))}
              </div>
            )}
          </Field>
        )}
        {customer?.overLimit && (
          <Callout tone="warning">Este cliente debe {formatMoney(customer.balanceCents)} y se pasó de su límite de crédito.</Callout>
        )}
        {customerId && (
          <>
            <Field label="Productos">
              <div style={{ position: 'relative' }}>
                <Input placeholder="Buscar producto" value={find} onChange={(e) => setFind(e.target.value)} aria-label="Buscar producto" />
                <Search size={16} aria-hidden style={{ position: 'absolute', right: 12, top: 12, opacity: 0.5 }} />
              </div>
            </Field>
            {matches.length > 0 && (
              <div style={{ display: 'grid', gap: 4 }}>
                {matches.map((p) => (
                  <button
                    key={p.productId}
                    type="button"
                    className="bf-pick"
                    onClick={() => {
                      setLines([...lines, { item: p, qty: '', price: moneyInput(p.priceCents) }]);
                      setFind('');
                    }}
                  >
                    <strong>{p.name}</strong>
                    <span className="bf-cell-sub">
                      {p.priceCents !== null
                        ? `${formatMoney(p.priceCents)} ${p.unitType === 'WEIGHT' ? 'el kilo' : 'c/u'}`
                        : 'Sin precio en su lista'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {lines.length > 0 && (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ width: 130 }}>Cantidad</th>
                      <th style={{ width: 150 }}>Precio</th>
                      <th style={{ textAlign: 'right' }}>Subtotal</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const q = parseQty(l.item.unitType, l.qty) ?? 0;
                      const p = parseMoney(l.price) ?? 0;
                      return (
                        <tr key={l.item.productId}>
                          <td>{l.item.name}</td>
                          <td>
                            <Input
                              inputMode="decimal"
                              placeholder={l.item.unitType === 'WEIGHT' ? 'kg' : 'unidades'}
                              value={l.qty}
                              onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                              aria-label={`Cantidad de ${l.item.name}`}
                            />
                          </td>
                          <td>
                            <Input
                              inputMode="decimal"
                              value={l.price}
                              disabled={!editPrices && l.item.priceCents !== null}
                              onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))}
                              aria-label={`Precio de ${l.item.name}`}
                            />
                          </td>
                          <td style={{ textAlign: 'right' }}>{formatMoney(lineTotal(l.item.unitType, q, p))}</td>
                          <td>
                            <button
                              type="button"
                              className="bf-order-del"
                              aria-label={`Quitar ${l.item.name}`}
                              onClick={() => setLines(lines.filter((_, j) => j !== i))}
                            >
                              <Trash2 size={14} aria-hidden />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
              <Field label="Entrega">
                <Select value={fulfillment} onChange={(e) => setFulfillment(e.target.value as 'DELIVERY' | 'PICKUP')}>
                  <option value="DELIVERY">Reparto</option>
                  <option value="PICKUP">Retira en el local</option>
                </Select>
              </Field>
              <Field label="Día">
                <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
              </Field>
              {fulfillment === 'DELIVERY' && (
                <Field label="Repartidor">
                  <Select value={driverUserId} onChange={(e) => setDriver(e.target.value)}>
                    <option value="">Después</option>
                    {(staff.data ?? [])
                      .filter((s) => s.canDeliver || s.userId === driverUserId)
                      .map((s) => (
                        <option key={s.userId} value={s.userId}>
                          {s.name}
                        </option>
                      ))}
                  </Select>
                </Field>
              )}
              <Field label="Comprobante">
                <Select value={doc} onChange={(e) => setDoc(e.target.value as WholesaleDocument | '')}>
                  <option value="">{`Por defecto (${WHOLESALE_DOCUMENT_LABELS[(customer?.defaultDocument ?? settings.data?.defaultDocument ?? 'REMITO') as WholesaleDocument]})`}</option>
                  <option value="REMITO">{WHOLESALE_DOCUMENT_LABELS.REMITO}</option>
                  <option value="INVOICE">{WHOLESALE_DOCUMENT_LABELS.INVOICE}</option>
                </Select>
              </Field>
              {own.length > 1 && (
                <Field label="Sale de">
                  <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    <option value="">{own.find((b) => b.id === settings.data?.originBranchId)?.name ?? 'Local por defecto'}</option>
                    {own.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
            <Field label="Notas">
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            {(doc || customer?.defaultDocument || settings.data?.defaultDocument || 'REMITO') === 'REMITO' && (
              <Callout tone="info">
                Sale con comprobante de entrega (documento interno, no válido como factura). La venta igual se factura: al entregar o después,
                juntando entregas, desde la cuenta del cliente.
              </Callout>
            )}
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <strong>Total estimado: {formatMoney(total)}</strong>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!customerId || !lines.length || missingPrice || badQty}
              onClick={() => save.mutate()}
            >
              Cargar pedido
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/** Detalle del pedido: preparar con los kilos reales, cobros, cambio de etapa y cancelación. */
export function OrderDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const staff = useStaff();
  const q = useQuery({ queryKey: ['wholesale', 'order', id], queryFn: () => api.get<WholesaleOrderDTO>(`/wholesale/orders/${id}`) });
  const [real, setReal] = useState<Record<string, string>>({});
  const [paying, setPaying] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['wholesale'] });
  const o = q.data;

  const prepare = useMutation({
    mutationFn: () =>
      api.post<WholesaleOrderDTO>(`/wholesale/orders/${id}/prepare`, {
        items: o!.items.map((i) => ({ itemId: i.id, qtyReal: parseQty(i.unitType, real[i.id] ?? qtyInput(i.unitType, i.qtyOrdered)) ?? 0 })),
      }),
    onSuccess: async (r) => {
      await refresh();
      toast.success(`Pedido #${r.number} listo`, `Total ${formatMoney(r.totalCents ?? 0)}. Se descontó el stock.`);
    },
    onError: (e) => toast.error('No se pudo preparar', errorMessage(e)),
  });
  const status = useMutation({
    mutationFn: (v: { status: WholesaleOrderStatus; driverUserId?: string | null }) =>
      api.patch<WholesaleOrderDTO>(`/wholesale/orders/${id}/status`, v),
    onSuccess: refresh,
    onError: (e) => toast.error('No se pudo cambiar', errorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()} title={o ? `Pedido #${o.number} · ${o.customerName}` : 'Pedido'} wide>
      {!o ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge tone={STATUS_TONES[o.status]}>{WHOLESALE_STATUS_LABELS[o.status]}</Badge>
            <span className="bf-cell-sub">
              {o.fulfillment === 'DELIVERY' ? `Reparto${o.driverName ? ` con ${o.driverName}` : ''}` : 'Retira en el local'} · sale de {o.branchName}
              {o.deliveryDate ? ` · ${formatDate(o.deliveryDate)}` : ''} · {WHOLESALE_DOCUMENT_LABELS[o.documentKind]}
              {o.priceListName ? ` · lista ${o.priceListName}` : ''}
            </span>
          </div>
          {o.documentKind === 'REMITO' && !['PENDING', 'PREPARING', 'CANCELED'].includes(o.status) && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <PrintNotesButton orderIds={[o.id]} />
              {!o.fiscal && <span className="bf-cell-sub">Sin facturar: se factura desde la cuenta del cliente.</span>}
            </div>
          )}
          <OrderRemitoBlock order={o} />
          {o.fiscal && (
            <Callout tone={o.fiscal.status === 'AUTHORIZED' ? 'success' : o.fiscal.status === 'REJECTED' ? 'danger' : 'info'}>
              Factura {o.fiscal.letter} {o.fiscal.number ?? ''} ·{' '}
              {o.fiscal.status === 'AUTHORIZED'
                ? 'autorizada'
                : o.fiscal.status === 'REJECTED'
                  ? 'rechazada: revisala en Facturación'
                  : 'pidiendo el CAE'}
            </Callout>
          )}
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Pedido</th>
                  <th>{o.status === 'PENDING' || o.status === 'PREPARING' ? 'Real (al pesar)' : 'Real'}</th>
                  <th style={{ textAlign: 'right' }}>Precio</th>
                  <th style={{ textAlign: 'right' }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {o.items.map((i) => (
                  <tr key={i.id}>
                    <td>
                      {i.productName} {i.priceOverridden && <Badge tone="warning">Precio a mano</Badge>}
                    </td>
                    <td>{qtyText(i.unitType, i.qtyOrdered)}</td>
                    <td>
                      {o.status === 'PENDING' || o.status === 'PREPARING' ? (
                        <Input
                          inputMode="decimal"
                          style={{ maxWidth: 120 }}
                          value={real[i.id] ?? qtyInput(i.unitType, i.qtyOrdered)}
                          onChange={(e) => setReal({ ...real, [i.id]: e.target.value })}
                          aria-label={`Real de ${i.productName}`}
                        />
                      ) : i.qtyReal !== null ? (
                        qtyText(i.unitType, i.qtyReal)
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(i.unitPriceCents)}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(i.lineTotalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span>
              {o.totalCents !== null ? 'Total' : 'Estimado'}: <strong>{formatMoney(o.totalCents ?? o.estimatedTotalCents)}</strong>
            </span>
            <span>
              Cobrado: <strong>{formatMoney(o.paidCents)}</strong>
            </span>
            <span>
              Falta: <strong>{formatMoney(o.dueCents)}</strong>
            </span>
          </div>
          {o.payments.length > 0 && (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cobro</th>
                    <th>Medio</th>
                    <th>Quién cobró</th>
                    <th style={{ textAlign: 'right' }}>Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {o.payments.map((p) => (
                    <tr key={p.id} style={p.voided ? { opacity: 0.5, textDecoration: 'line-through' } : undefined}>
                      <td>
                        {WHOLESALE_STAGE_LABELS[p.stage]} <span className="bf-cell-sub">{formatDateTime(p.createdAt)}</span>
                      </td>
                      <td>{WHOLESALE_METHOD_LABELS[p.method]}</td>
                      <td>
                        {p.collectedByName ?? p.createdByName ?? '—'}{' '}
                        {p.collectedBy && !p.voided && <Badge tone={p.settled ? 'success' : 'warning'}>{p.settled ? 'Rendido' : 'Sin rendir'}</Badge>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(p.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {o.notes && <p className="bf-cell-sub">Notas: {o.notes}</p>}
          {o.cancelReason && <Callout tone="danger">Cancelado: {o.cancelReason}</Callout>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {o.status !== 'CANCELED' && o.status !== 'DELIVERED' && o.status !== 'SETTLED' && (
              <Button variant="ghost" onClick={() => setCanceling(true)}>
                Cancelar pedido
              </Button>
            )}
            {o.status !== 'CANCELED' && o.dueCents > 0 && <Button onClick={() => setPaying(true)}>Registrar cobro</Button>}
            {o.status === 'PENDING' && (
              <Button onClick={() => status.mutate({ status: 'PREPARING' })} loading={status.isPending}>
                Pasar a preparación
              </Button>
            )}
            {(o.status === 'PENDING' || o.status === 'PREPARING') && (
              <Button variant="primary" loading={prepare.isPending} onClick={() => prepare.mutate()}>
                Preparado: descontar stock
              </Button>
            )}
            {o.status === 'READY' && o.fulfillment === 'DELIVERY' && (
              <Button variant="primary" onClick={() => status.mutate({ status: 'ON_ROUTE' })} loading={status.isPending}>
                Sale a reparto
              </Button>
            )}
            {(o.status === 'ON_ROUTE' || (o.status === 'READY' && o.fulfillment === 'PICKUP' && manage)) && (
              <Button variant="primary" onClick={() => status.mutate({ status: 'DELIVERED' })} loading={status.isPending}>
                {o.fulfillment === 'PICKUP' ? 'Retirado' : 'Entregado'}
              </Button>
            )}
          </div>
          {paying && (
            <PaymentDialog order={o} staffList={staff.data ?? []} onClose={() => setPaying(false)} onDone={() => (setPaying(false), refresh())} />
          )}
          {canceling && <CancelDialog order={o} onClose={() => setCanceling(false)} onDone={() => (setCanceling(false), refresh())} />}
        </div>
      )}
    </Dialog>
  );
}

function PaymentDialog({
  order,
  staffList,
  onClose,
  onDone,
}: {
  order: WholesaleOrderDTO;
  staffList: { userId: string; name: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const [stage, setStage] = useState<'ADVANCE' | 'ON_DELIVERY' | 'ACCOUNT'>(order.status === 'ON_ROUTE' || !manage ? 'ON_DELIVERY' : 'ADVANCE');
  const [method, setMethod] = useState<WholesalePaymentMethod>('CASH');
  const [amount, setAmount] = useState(moneyInput(order.dueCents));
  const [collectedBy, setCollectedBy] = useState(order.driverUserId ?? '');
  const [reference, setReference] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.post('/wholesale/payments', {
        customerId: order.customerId,
        orderId: order.id,
        stage,
        method,
        amountCents: parseMoney(amount) ?? 0,
        collectedBy: stage === 'ON_DELIVERY' ? collectedBy || null : null,
        reference: reference || null,
      }),
    onSuccess: () => {
      toast.success('Cobro registrado');
      onDone();
    },
    onError: (e) => toast.error('No se pudo registrar', errorMessage(e)),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Cobro del pedido #${order.number}`}
      description="Lo que no se cobra queda en la cuenta corriente del cliente al entregar."
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="Cuándo">
          <Select value={stage} onChange={(e) => setStage(e.target.value as typeof stage)} disabled={!manage}>
            <option value="ADVANCE">Anticipo (antes de enviar)</option>
            <option value="ON_DELIVERY">Al entregar (lo cobra el repartidor)</option>
          </Select>
        </Field>
        <Field label="Medio">
          <Select value={method} onChange={(e) => setMethod(e.target.value as WholesalePaymentMethod)}>
            {WHOLESALE_PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {WHOLESALE_METHOD_LABELS[m]}
              </option>
            ))}
          </Select>
        </Field>
        {stage === 'ON_DELIVERY' && manage && (
          <Field label="Quién cobró" hint="La plata queda a su nombre hasta que la rinde.">
            <Select value={collectedBy} onChange={(e) => setCollectedBy(e.target.value)}>
              <option value="">Elegí</option>
              {staffList.map((s) => (
                <option key={s.userId} value={s.userId}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Importe">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Referencia (opcional)" hint="Número de transferencia, de cheque…">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={save.isPending} disabled={!parseMoney(amount)} onClick={() => save.mutate()}>
            Registrar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CancelDialog({ order, onClose, onDone }: { order: WholesaleOrderDTO; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/wholesale/orders/${order.id}/cancel`, { reason }),
    onSuccess: () => {
      toast.success(`Pedido #${order.number} cancelado`);
      onDone();
    },
    onError: (e) => toast.error(isApiError(e, 'CONFLICT') ? 'No se puede cancelar' : 'No se pudo cancelar', errorMessage(e)),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Cancelar el pedido #${order.number}`}
      description={order.status === 'READY' || order.status === 'ON_ROUTE' ? 'La mercadería vuelve al stock.' : undefined}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="Motivo">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={onClose}>Volver</Button>
          <Button variant="danger" loading={save.isPending} disabled={reason.trim().length < 3} onClick={() => save.mutate()}>
            Cancelar pedido
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
